import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

export const loginUser = async (
  username: string,
  password: string
): Promise<{
  user: {
    codigoVoluntario: string | null;
    userType: string;
    mustChangePassword: boolean;
  };
  token: string;
  refreshToken: string;
}> => {
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/auth/login`,
      { username, password },
      { withCredentials: true }
    );

    const { user, token, refreshToken } = response.data;
    return { user, token, refreshToken };
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao fazer login');
    } else {
      throw new Error('Erro desconhecido');
    }
  }
};

export const firstLoginPasswordChange = async (
  currentPassword: string,
  newPassword: string,
  confirmPassword: string
): Promise<{
  token: string;
  user: {
    codigoVoluntario: string | null;
    userType: string;
    mustChangePassword: boolean;
  };
}> => {
  try {
    const token = localStorage.getItem('authToken');

    const response = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/auth/first-login-password`,
      { currentPassword, newPassword, confirmPassword },
      {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      }
    );

    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao trocar a senha');
    } else {
      throw new Error('Erro desconhecido');
    }
  }
};

export const registerUser = async (userData: {
  username: string;
  email: string;
  fullName: string;
  password: string;
  confirmPassword: string;
  userType: string;
  /** at-81: todos os perfis do usuário. O `userType` é o principal e sempre entra na lista. */
  papeis?: string[];
  isAdmin?: boolean;
  codigoVoluntario?: number | null;
  codigoRegional?: number | null;
}) => {
  try {
    const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/auth/register`, userData, {
      withCredentials: true,
    });

    const { token, refreshToken } = response.data;
    return { token, refreshToken };
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao registrar usuário');
    } else {
      throw new Error('Erro desconhecido');
    }
  }
};

export const validateResetToken = async (token: string) => {
  try {
    const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/auth/validate-reset-token`, {
      token,
    });
    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Token inválido ou expirado');
    } else {
      throw new Error('Erro desconhecido ao validar o token');
    }
  }
};

export const resetPassword = async (token: string, newPassword: string, confirmPassword: string) => {
  try {
    const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/auth/reset-password`, {
      token,
      newPassword,
      confirmPassword,
    });
    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao redefinir a senha');
    } else {
      throw new Error('Erro desconhecido ao redefinir a senha');
    }
  }
};

export const forgotPassword = async (email: string) => {
  try {
    const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/auth/forgot-password`, {
      email,
    });

    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao solicitar redefinição de senha');
    } else {
      throw new Error('Erro desconhecido ao solicitar redefinição de senha');
    }
  }
};

export const verifyPassword = async (password: string): Promise<{ valid: boolean }> => {
  const token = localStorage.getItem('authToken');
  const response = await axios.post(
    `${process.env.REACT_APP_BACKEND_URL}/auth/verify-password`,
    { password },
    {
      withCredentials: true,
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    }
  );
  return response.data;
};

/**
 * Encerra a sessão no servidor (at-72).
 *
 * É a única forma de apagar o cookie `refreshToken`, que é `httpOnly` e vive 7 dias:
 * JavaScript não o alcança. A rota também apaga os refresh tokens do usuário no banco e
 * grava o hash do access token em `revoked_tokens` (revogação persistida do at-35/ADR 0016).
 *
 * `withCredentials` é obrigatório: o handler lê o token do COOKIE, não do header. O header
 * vai junto de qualquer forma, para o caso de essa peculiaridade ser corrigida depois.
 */
export const logoutUser = async (): Promise<void> => {
  const token = await tokenValidoParaSair(localStorage.getItem('authToken'));
  // Sessão já encerrada no servidor (token vencido e refresh recusado): não há o que revogar,
  // e chamar a rota só gravaria uma "falha de logout" no log geral.
  if (token === null) return;
  await axios.post(
    `${process.env.REACT_APP_BACKEND_URL}/auth/logout`,
    {},
    {
      withCredentials: true,
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    }
  );
};

/**
 * O access token dura 2h, e o logout não passa pelo interceptor de refresh (ele ignora `/auth/`).
 * Com o token vencido, a rota respondia 401 em todo "Sair" feito depois de 2h de tela aberta.
 * Aqui o token vencido é renovado antes; o refresh também grava o cookie novo, que é o que a
 * rota lê.
 *
 * Devolve `null` quando o servidor RECUSA o refresh (401/403): a sessão já não vale lá.
 * Falha de rede no refresh sobe como erro, e a tela avisa como antes.
 */
const tokenValidoParaSair = async (token: string | null): Promise<string | null | undefined> => {
  if (!token) return undefined;
  let venceEm: number | undefined;
  try {
    venceEm = jwtDecode<{ exp?: number }>(token).exp;
  } catch {
    // Token ilegível: segue para a rota, que decide (silêncio deliberado, não é chamada de API).
    return token;
  }
  if (!venceEm || venceEm * 1000 > Date.now() + 30_000) return token;
  try {
    const resposta = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/auth/refresh`,
      {},
      { withCredentials: true }
    );
    const novo: string = resposta.data.token;
    localStorage.setItem('authToken', novo);
    return novo;
  } catch (erro: any) {
    const status = erro?.response?.status;
    if (status === 401 || status === 403) return null;
    throw erro;
  }
};
